<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { Icon } from "../../../../components";

const props = defineProps({
  initialStartDate: {
    type: Date,
    default: () => {
      // Default to the range shown in the image if no start date is provided.
      // This calculates the date for Jan 6, 2023.
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() - 3);
      return defaultDate;
    },
  },
  initialSelectedDate: {
    type: Date,
    default: () => new Date(), // The full Date object that is initially selected
  },
  disabledDates: {
    type: Array,
    default: () => [], // Array of full Date objects to disable
  },
});

const emit = defineEmits(["date-selected", "range-changed"]);

const startDate = ref(props.initialStartDate); // The first day of the current 7-day view
const selectedDate = ref(props.initialSelectedDate);
const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Reactive month and year based on the current start date
const currentMonth = computed(() => startDate.value.getMonth());
const currentYear = computed(() => startDate.value.getFullYear());

const monthName = computed(() => {
  const date = new Date(currentYear.value, currentMonth.value, 1);
  return date.toLocaleString("en-US", { month: "long" });
});

const visibleDates = computed(() => {
  const datesArray = [];
  let currentDate = new Date(startDate.value); // Clone to avoid modifying startDate.value directly

  for (let i = 0; i < 7; i++) {
    const dayOfMonth = currentDate.getDate();
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const isSelected =
      selectedDate.value &&
      selectedDate.value.getDate() === dayOfMonth &&
      selectedDate.value.getMonth() === month &&
      selectedDate.value.getFullYear() === year;

    const isDisabled = props.disabledDates.some(
      (disabledDate) =>
        disabledDate.getDate() === dayOfMonth &&
        disabledDate.getMonth() === month &&
        disabledDate.getFullYear() === year
    );

    datesArray.push({
      fullDate: new Date(currentDate), // Store a copy of the Date object
      dayOfMonth: dayOfMonth,
      selected: isSelected,
      disabled: isDisabled,
    });
    currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
  }
  return datesArray;
});

const selectDate = (fullDate) => {
  console.log(fullDate);
  const isDisabled = props.disabledDates.some(
    (disabledDate) =>
      disabledDate.getDate() === fullDate.getDate() &&
      disabledDate.getMonth() === fullDate.getMonth() &&
      disabledDate.getFullYear() === fullDate.getFullYear()
  );

  if (!isDisabled) {
    selectedDate.value = fullDate;
    emit("date-selected", fullDate);
  }
};

const navigateSevenDays = (direction) => {
  const newStartDate = new Date(startDate.value);
  newStartDate.setDate(newStartDate.getDate() + direction * 7);
  startDate.value = newStartDate;
  emit("range-changed", {
    startDate: newStartDate,
    endDate: new Date(
      newStartDate.getFullYear(),
      newStartDate.getMonth(),
      newStartDate.getDate() + 6
    ),
  });
};

// Watch for changes in initialSelectedDate prop
watch(
  () => props.initialSelectedDate,
  (newVal) => {
    selectedDate.value = newVal;
  }
);

// Watch for changes in initialStartDate prop
watch(
  () => props.initialStartDate,
  (newVal) => {
    startDate.value = newVal;
  }
);

// Set default selected date if not provided and matches the image scenario (Jan 9, 2023 within Jan 6-12 range)
onMounted(() => {
  if (!props.initialSelectedDate) {
    const exampleSelectedDate = new Date(2023, 0, 9); // Jan 9, 2023
    const exampleStartDate = new Date(2023, 0, 6); // Jan 6, 2023

    // Check if the current startDate matches the example's range start
    const isExampleRange =
      startDate.value.getDate() === exampleStartDate.getDate() &&
      startDate.value.getMonth() === exampleStartDate.getMonth() &&
      startDate.value.getFullYear() === exampleStartDate.getFullYear();

    // Set selected date if it's within the example range AND no initialSelectedDate was provided
    if (isExampleRange) {
      selectedDate.value = exampleSelectedDate;
    }
  }
});
</script>

<template>
  <div class="">
    <div
      class="flex items-center gap-2 justify-between pb-[8px] mb-[8px] border-b-1 border-t-0 border-r-0 border-l-0 border-solid border-[#EAECF0]"
    >
      <h2 class="text-gray-700 text-[16px] m-0">
        {{ monthName }} {{ currentYear }}
      </h2>
      <div class="flex items-center gap-0.5">
        <button
          @click="navigateSevenDays(-1)"
          class="bg-white border-none cursor-pointer inline-flex p-0"
        >
          <Icon name="arrowLeft" class="text-gray-500 w-5 h-5" />
        </button>
        <button
          @click="navigateSevenDays(1)"
          class="bg-white border-none cursor-pointer inline-flex p-0"
        >
          <Icon name="arrowRight" class="text-gray-500 w-5 h-5" />
        </button>
      </div>
    </div>
    <div
      class="grid grid-cols-7 gap-1.5 text-[14px] text-gray-500 font-medium text-center py-[5px]"
    >
      <span v-for="day in daysOfWeek" :key="day">{{ day }}</span>
    </div>
    <div
      class="grid grid-cols-7 gap-1.5 text-[14px] text-gray-500 font-medium text-center pt-[5px]"
    >
      <div
        v-for="dateItem in visibleDates"
        :key="dateItem.fullDate.getTime()"
        :class="[
          'date-box',
          { selected: dateItem.selected, disabled: dateItem.disabled },
        ]"
        class="cursor-pointer py-[5px] border border-solid border-gray-200 rounded-[4px]"
        @click="selectDate(dateItem.fullDate)"
      >
        {{ dateItem.dayOfMonth }}
      </div>
    </div>
  </div>
</template>

<style scoped></style>
